import type { ReactNode } from "react";
import { printSignFile } from "../api/client";
import { docWhen, signerLabel, statusClass, statusLabel } from "../lib/format";
import type { SignDoc } from "../types";
import { IcoArchive, IcoCopy, IcoDownload, IcoEye, IcoLink, IcoPen, IcoPrint, IcoSend, IcoShield } from "../ui/icons";

function Tip({
  label,
  onClick,
  disabled = false,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button type="button" className="sg-tip" data-tip={label} aria-label={label} disabled={disabled} onClick={onClick}>
      {children}
    </button>
  );
}

export function SubmissionList({
  docs,
  busy,
  onCopy,
  onView,
  onArchive,
  onTrace,
  onPieces,
  onClone,
  onMail,
  onDownload,
  mailingId = 0,
}: {
  docs: SignDoc[];
  busy: boolean;
  onCopy: (d: SignDoc) => void;
  onView: (d: SignDoc) => void;
  onArchive: (d: SignDoc) => void;
  onTrace: (d: SignDoc) => void;
  onPieces: (d: SignDoc) => void;
  onClone: (d: SignDoc) => void;
  onMail: (d: SignDoc) => void;
  onDownload: (d: SignDoc) => void;
  mailingId?: number;
}) {
  if (busy && docs.length === 0) {
    return <p className="sg-empty">Chargement des soumissions…</p>;
  }
  if (!busy && docs.length === 0) {
    return (
      <div className="sg-empty is-soft">
        <h2>Aucune soumission</h2>
        <p>Les PDF envoyés ou encore en brouillon apparaissent ici, tous dossiers confondus.</p>
      </div>
    );
  }

  return (
    <div className="sg-subs">
      {docs.map((d) => {
        const mailing = mailingId === d.id;
        const draft = d.status === "draft";
        const mailLabel = draft ? "Envoyer" : mailing ? "Envoi…" : "Renvoyer";
        const hasPieces = !draft && ((d.asks?.length ?? 0) > 0 || (d.pieces?.length ?? 0) > 0);
        return (
          <article key={d.id} className="sg-sub" onClick={() => onView(d)}>
            <div className="sg-sub__who">
              <strong>{d.title}</strong>
              <small>
                {signerLabel(d)} · {docWhen(d)}
                {d.signer_email ? ` · ${d.signer_email}` : ""}
              </small>
            </div>
            <em className={statusClass(d)}>{statusLabel(d)}</em>
            <div className="sg-sub__acts" onClick={(e) => e.stopPropagation()}>
              <Tip label={mailLabel} disabled={mailing} onClick={() => !mailing && onMail(d)}>
                <IcoSend />
              </Tip>
              {!draft && d.status !== "signed" ? (
                <button type="button" className="sg-linkcopy" onClick={() => onCopy(d)}>
                  <IcoLink />
                  Copier le lien
                </button>
              ) : null}
              <Tip label={draft ? "Modifier" : "Voir"} onClick={() => onView(d)}>
                {draft ? <IcoPen /> : <IcoEye />}
              </Tip>
              <Tip label="Cloner" onClick={() => onClone(d)}>
                <IcoCopy />
              </Tip>
              {hasPieces ? (
                <Tip label="Pièces" onClick={() => onPieces(d)}>
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M15.4 7.2 8.7 14a2.4 2.4 0 0 0 3.4 3.4l7.2-7.2a3.6 3.6 0 0 0-5.1-5.1L7 12.3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Tip>
              ) : null}
              {!draft ? (
                <Tip label="Imprimer" onClick={() => printSignFile(d)}>
                  <IcoPrint />
                </Tip>
              ) : null}
              {!draft ? (
                <Tip label="Télécharger" onClick={() => onDownload(d)}>
                  <IcoDownload />
                </Tip>
              ) : null}
              {!draft ? (
                <Tip label="Intégrité" onClick={() => onTrace(d)}>
                  <IcoShield />
                </Tip>
              ) : null}
              {d.status !== "signed" ? (
                <Tip label="Archiver" onClick={() => onArchive(d)}>
                  <IcoArchive />
                </Tip>
              ) : null}
            </div>
          </article>
        );
      })}
    </div>
  );
}
